import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useDebouncedCallback from "@shared/utils/hooks/useDebouncedCallback";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useClientsData,
  useExchangesMetadata,
  usePrevious,
  useSymbolsMetadata,
} from "@shared/utils/hooks/misc";
import {
  AmountType,
  BaseStrategyData,
  BotData,
  BotGeneralData,
  BotOptions,
  BotSymbolPriceData,
  BotType,
  BuiltInStrategyData,
  ClientData,
  CustomStrategyData,
  DisplayResultMessage,
  LanguageNameSpaces,
  LongShort,
  MarginTypes,
  NumberOption,
  OrderBaseline,
  OrderTypes,
  OverrideFieldType,
  PrecisionType,
  StrategyData,
  StrategyStyleType,
  StrategyType,
} from "@shared/types";
import {
  escapeRegExp,
  getPairFromSymbol,
  updateItems,
} from "@shared/utils/misc";
import {
  BotFormContext,
  PreserveBotFormValuesContext,
} from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  BaseBotFormValues,
  BotFormType,
  BotFormValues,
  BulkEditBotData,
  EntryGraceBaseline,
  Error,
  HedgingMode,
  MultiValue,
  OverrideType,
  ParsedDcaStrategyData,
  PercentageBaseType,
  RestoreChangesType,
  Steps,
  StrategyTabs,
  ToggleField,
  UseSharedBotNameInputProps,
  useSharedDefaultOrderProps,
} from "@shared/features/bots/BotForm/common/types";
import {
  channelValue,
  DEFAULT_AMOUNT_PERCENT,
  DEFAULT_FIRST_ENTRY_TAKE_PROFIT,
  DEFAULT_STOP,
  DEFAULT_TAKE_PROFIT,
  FIELD_TO_PARENT_FIELD,
  FORM_KEY_TO_OVERRIDE_FIELD,
  FORM_KEY_TO_TOGGLE_FIELD,
  MAX_BACKEND_BOT_NAME_LENGTH,
  MAX_BOT_NAME_LENGTH,
  MAX_CHANNEL_CONFIGURATION_PERCENT,
  MIN_CONSECUTIVE_GRIDS_DISTANCE,
  MULTI_VALUE_TOGGLE_BOT_KEYS,
  multiToggleValue,
  multiValue,
  ORDER_BASELINE_TO_DISPLAY,
  RESTORE_CHANGES_TYPE_TO_DISPLAY,
  STOP_BASELINE_TITLE,
  STOP_PERCENT_TITLE,
  STOP_TYPE_OPTIONS,
  TAKE_PROFIT_BASELINE_TITLE,
  TAKE_PROFIT_PERCENT_TITLE,
  TOGGLE_FIELD_TO_OVERRIDE_KEY,
  TOGGLE_FIELD_TO_STRATEGY_FIELDS,
} from "@shared/features/bots/BotForm/common/constants";
import { isSymbol } from "@shared/types/utils";
import {
  isEqual,
  isNumber,
  isObject,
  mapValues,
  pickBy,
  unionBy,
  xor,
} from "lodash";
import {
  getActiveEntriesAmountError,
  getBotMinAmount,
  getBotValues,
  getDefaultBotStrategy,
  getDefaultOrderPrice,
  getIsReverse,
  getBotMarginOptions,
  getHedgingValidationOrders,
  getMaxBotAmount,
  getMaxStopPercent,
  hasMultiValues,
  getMinEntryOrderAmountError,
  getMinStopPercent,
  getParentFieldChecked,
  getStrategyOrders,
  isHedgingRangeMode,
  setBotFormValues,
  splitGridBotEntries,
  validateDefaultStopOrder,
  validateDefaultTakeProfit,
} from "@shared/features/bots/BotForm/common/utils";
import { useAccountAmount } from "@shared/features/onboarding/hooks";
import {
  getAmountTypeSuffix,
  getIsPercentAmountType,
  makeNameUnique,
} from "@shared/features/bots/utils";
import useAccountMainCoins from "@shared/features/onboarding/hooks/useAccountMainCoins";
import {
  BOT_TYPE_TO_DISPLAY_NAME,
  TAB_TO_OBJECT_NAME,
} from "@shared/features/bots/constants";
import { BotAmountData } from "@shared/features/bots/types";
import { getDefaultAmountPerTradeValue } from "@shared/common/utils";
import usePricesSocket from "@shared/services/socket/usePricesSocket";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import {
  getOrdersWithExisting,
  getParsedEntries,
} from "@shared/features/tradingTerminal/common/utils";
import {
  FALLBACK_MAIN_COINS,
  USD_PRECISION,
} from "@shared/features/onboarding/constants";
import { toFixed } from "@shared/utils/numbers";
import { BotBaseStrategiesContext } from "@shared/features/bots/BotForm/Strategies/BotBaseStrategiesProvider";
import { BotStrategiesContext } from "@shared/features/bots/BotForm/Strategies/BotStrategies.context";
import useFormHelperData from "@shared/features/bots/BotForm/common/hooks/useFormHelperData";
import { getIsHighRisk } from "@shared/features/newMarketplace/utils";
import useGroupMainCoinsSignalsCount from "@shared/features/bots/BotForm/useGroupMainCoinsSignalsCount";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { joinWords } from "@shared/common/i18n/utils";
import { MARGIN_TYPE_TO_DISPLAY } from "@shared/common/constants";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";

export const useUpdateStrategyPriceError = (
  orderType: OrderTypes,
  isPriceError: boolean | undefined,
  isGridsPriceDistanceError: boolean | undefined,
  isFocused: boolean
) => {
  const { addError, removeError, errors } = useContext(BotFormContext) || {};

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    const errorType =
      orderType === OrderTypes.ENTRY
        ? Error.ENTRY_PRICES
        : Error.TAKE_PROFIT_PRICES;
    const errorExisted = errors?.includes(Error.GENERAL);
    if (isPriceError && !errorExisted) {
      addError?.(errorType);
    } else {
      removeError?.(errorType);
    }
  }, [isPriceError, isFocused, orderType, errors]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    const errorType = Error.CONSECUTIVE_GRIDS_PRICE_DISTANCE;
    const errorExisted = errors?.includes(Error.GENERAL);
    if (isGridsPriceDistanceError && !errorExisted) {
      addError?.(errorType);
    } else {
      removeError?.(errorType);
    }
  }, [isGridsPriceDistanceError, isFocused, orderType, errors]);
};

export const useValidateBotName = (
  botName?: string | MultiValue,
  currentBotId?: number
) => {
  const { botType, botFormType, botGeneralData, isChannelConfiguration } =
    useContext(BotFormContext)!;
  const { data: botsNames } = sharedLib.sharedApi.bots.useGetBotsNamesQuery(
    botType && !isChannelConfiguration ? { bot_type: botType } : skipToken
  );
  const isDuplicate = botFormType === BotFormType.DUPLICATE;
  let maxNameLength = MAX_BOT_NAME_LENGTH;
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.BOTS);

  if (
    [BotFormType.DUPLICATE, BotFormType.EDIT].includes(botFormType) &&
    botGeneralData &&
    "name" in botGeneralData
  ) {
    let originalName = botGeneralData.name;
    if (isDuplicate) originalName += ` ${t("labels.copy_suffix")}`;
    maxNameLength = Math.max(MAX_BOT_NAME_LENGTH, originalName.length);
    maxNameLength = Math.min(MAX_BACKEND_BOT_NAME_LENGTH, maxNameLength);
  }
  const nameError = useMemo(() => {
    if (isSymbol(botName)) return;
    if (!botName) return t("messages.errors.choose_bot_name");
    else if (botName.length > maxNameLength)
      return t("messages.errors.bot_name_too_long", { max: maxNameLength });
    const botWithSameNameExists =
      botsNames &&
      Object.keys(botsNames).some((botIdStr) => {
        const botId = parseInt(botIdStr);
        return (
          botsNames[botId].toLowerCase() === botName.toLowerCase() &&
          botId !== currentBotId
        );
      });
    if (botWithSameNameExists && botType !== undefined) {
      return t("messages.errors.duplicate_bot_name", {
        botType: t(BOT_TYPE_TO_DISPLAY_NAME[botType]),
      });
    }
  }, [botName, botsNames, maxNameLength, botType, i18n.language]);

  return {
    botsNames,
    nameError,
  };
};

export const useSharedBotNameInput = ({
  currentBotId,
  initialize,
}: UseSharedBotNameInputProps) => {
  const preserveValues = useContext(PreserveBotFormValuesContext);
  const {
    botType,
    name,
    setName,
    symbols,
    useSymbolFromAlert,
    bulkActionBotsData,
    botFormType,
    subGroup,
    isCopyToWorkspace,
    isPublicBotApplied,
  } = useContext(BotFormContext)!;
  const [localName, setLocalName] = useState<string | MultiValue>(name);
  const debouncedSetName = useDebouncedCallback({
    callback: setName,
  });
  const { botsNames, nameError } = useValidateBotName(localName, currentBotId);
  const [initializedGroupName, setInitializedGroupName] = useState<string>();
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const isEdit = botFormType === BotFormType.EDIT || isBulkEdit;
  const isTvBot =
    botType === BotType.TRADING_VIEW ||
    botType === BotType.TRADING_VIEW_SIGNALS;
  const isSignalsBot = botType === BotType.SIGNAL;
  const symbol =
    isSymbol(symbols) || isSignalsBot || useSymbolFromAlert
      ? undefined
      : symbols[0];
  const lastSymbol = usePrevious(symbol);
  const subGroupName = subGroup?.name;
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const botDisplayName =
    botType === BotType.TRADING_VIEW || botType === BotType.TRADING_VIEW_SIGNALS
      ? t("labels.trading_view_short")
      : t(BOT_TYPE_TO_DISPLAY_NAME[botType]);
  const botTypeName = joinWords([botDisplayName, t("labels.bot_suffix")]);

  useEffect(() => {
    setLocalName(name);
  }, [name]);

  const handleSetName = (updatedName: string) => {
    setLocalName(updatedName);
    debouncedSetName(updatedName);
  };

  const getWrappedRegex = (value: string) => {
    return new RegExp(`(^|\\s)${value}($|\\s)`);
  };

  const getSubGroupShortName = (name?: string) => {
    const numCharsLeft = MAX_BOT_NAME_LENGTH - ` ${botTypeName}`.length;
    const wordsArray = name?.split(" ");
    while (
      wordsArray &&
      wordsArray.length > 1 &&
      (wordsArray.join(" ").length > numCharsLeft ||
        wordsArray?.[wordsArray.length - 1] === botDisplayName)
    ) {
      wordsArray.pop();
    }
    if (wordsArray && wordsArray.length <= 1) {
      return wordsArray[0]?.substring(0, numCharsLeft);
    } else {
      return wordsArray?.join(" ");
    }
  };

  const getDefaultBotName = () => {
    let namePrefix: string | undefined;
    if (isSignalsBot) {
      namePrefix = subGroupName
        ? `${getSubGroupShortName(subGroupName)} `
        : undefined;
    } else if (symbol) {
      namePrefix = `${symbol} `;
    }
    const defaultName = `${namePrefix ?? ""}${botTypeName}`;
    return makeNameUnique(defaultName, botsNames);
  };

  useEffect(() => {
    if (!botsNames || isSymbol(name) || isEdit) return;
    let updatedName: string = "";
    if (initialize && !name && (isSignalsBot || useSymbolFromAlert || symbol)) {
      updatedName = getDefaultBotName();
      if (subGroupName) {
        setInitializedGroupName(subGroupName);
      }
    } else if (
      name &&
      (isTvBot || (lastSymbol && symbol)) &&
      (!preserveValues || lastSymbol !== undefined) &&
      symbol !== lastSymbol
    ) {
      if (isPublicBotApplied) {
        updatedName = getDefaultBotName();
      } else {
        if (lastSymbol) {
          const lastSymbolRegex = getWrappedRegex(escapeRegExp(lastSymbol));
          updatedName = name.replace(lastSymbolRegex, `$1${symbol || ""}$2`);
        } else {
          updatedName = `${symbol} ${name}`;
        }
        updatedName.replace(` ${t("labels.copy_suffix")}`, "").trim();
      }
    } else if (
      name &&
      isSignalsBot &&
      subGroupName &&
      (!initializedGroupName || subGroupName !== initializedGroupName)
    ) {
      const groupName = getSubGroupShortName(subGroupName);
      const lastGroupName = getSubGroupShortName(initializedGroupName) || "";
      const lastGroupNameRegex = getWrappedRegex(escapeRegExp(lastGroupName));
      if (lastGroupName && lastGroupNameRegex.test(name)) {
        updatedName = name.replace(lastGroupNameRegex, `$1${groupName}$2`);

        if (makeNameUnique(updatedName, botsNames) !== updatedName) {
          updatedName = updatedName.replace(` ${t("labels.copy_suffix")}`, "");
        }
      } else if (
        !initializedGroupName &&
        groupName &&
        !name.includes(groupName)
      ) {
        updatedName = `${groupName} ${name}`;
      } else {
        updatedName = name;
      }
      setInitializedGroupName(subGroupName);
    } else if (isCopyToWorkspace) {
      updatedName = name;
    }
    updatedName = updatedName.trim();
    if (updatedName) {
      setName(makeNameUnique(updatedName, botsNames));
    }
  }, [initialize, symbol, subGroupName, botsNames, isPublicBotApplied]);

  const nameIsMultiValue = isSymbol(localName);

  return {
    handleSetName,
    flushName: debouncedSetName.flush,
    nameError,
    botName: nameIsMultiValue ? "" : localName,
    placeholder: nameIsMultiValue ? t("labels.multi") : undefined,
    disabled: isBulkEdit && (bulkActionBotsData?.length || 0) > 1,
  };
};

interface useUpdateFormStrategyAndErrorsProps {
  orderType: OrderTypes;
  isFocused?: boolean;
  isValid: boolean;
  tab?: StrategyTabs;
  shouldUpdate?: boolean;
  targets?: CustomStrategyData["targets"];
  parsedDcaData: ParsedDcaStrategyData;
  templateStrategy?: BuiltInStrategyData["builtin_strategy_type"];
}

export const useUpdateFormStrategyAndErrors = ({
  orderType,
  isFocused,
  isValid,
  targets,
  tab,
  shouldUpdate,
  parsedDcaData,
  templateStrategy,
}: useUpdateFormStrategyAndErrorsProps) => {
  const {
    addError,
    removeError,
    setEntryStrategy,
    setTakeProfitStrategy,
    entryStrategy,
    takeProfitStrategy,
    checkedFields,
    botType,
    isChannelConfiguration,
  } = useContext(BotFormContext) || {};
  const isSignalsBot = botType === BotType.SIGNAL;
  const isHedgingBot = botType === BotType.HEDGING;
  const isCustomTab = tab === StrategyTabs.CUSTOM;
  const isDcaTab = tab === StrategyTabs.DCA;
  const {
    numOrders,
    numActiveOrders,
    amountScale,
    priceDifference,
    priceScale,
    firstOrderPercent,
    baseline,
  } = parsedDcaData;

  const isToggledOn = checkedFields?.has(
    orderType === OrderTypes.ENTRY
      ? ToggleField.ENTRIES
      : ToggleField.TAKE_PROFIT_STRATEGY
  );
  const pricesToggledOn = checkedFields?.has(
    orderType === OrderTypes.ENTRY
      ? ToggleField.ENTRY_PRICE_STRATEGY
      : ToggleField.TAKE_PROFIT_PRICE_STRATEGY
  );
  const ratiosToggledOn = checkedFields?.has(
    orderType === OrderTypes.ENTRY
      ? ToggleField.ENTRY_RATIOS
      : ToggleField.TAKE_PROFIT_RATIOS
  );
  const firstOrderToggledOn =
    orderType === OrderTypes.ENTRY &&
    checkedFields?.has(ToggleField.FIRST_ENTRY);

  useEffect(() => {
    const setStrategy =
      orderType === OrderTypes.ENTRY ? setEntryStrategy : setTakeProfitStrategy;
    const strategy =
      orderType === OrderTypes.ENTRY ? entryStrategy : takeProfitStrategy;
    const errorType =
      orderType === OrderTypes.ENTRY
        ? Error.ENTRY_STRATEGY
        : Error.TAKE_PROFIT_STRATEGY;

    if (
      !isFocused ||
      isSymbol(strategy) ||
      (isSymbol(strategy?.strategy_style) && !isSignalsBot) ||
      !shouldUpdate
    ) {
      removeError?.(errorType);
      return;
    }

    const isChannelRatios = isSignalsBot && isSymbol(strategy?.strategy_style);
    const isChannelPrices = isSignalsBot && isSymbol(strategy?.price_scale);
    const baseOrderData: BaseStrategyData = {
      price_difference: priceDifference,
      price_scale: priceScale,
      number_of_active_orders: numActiveOrders,
      number_of_orders:
        isChannelConfiguration && !ratiosToggledOn ? undefined : numOrders,
      baseline: isChannelPrices ? channelValue : baseline,
    };
    let strategyData: StrategyData | undefined;
    if (tab === StrategyTabs.BUILT_IN) {
      strategyData = {
        ...baseOrderData,
        strategy_style: StrategyStyleType.BUILT_IN,
        builtin_strategy_type: isChannelRatios
          ? channelValue
          : templateStrategy ?? StrategyType.EVENLY_DIVIDED,
      };
      if (isChannelRatios) strategyData.amount_scale = channelValue;
      if (!ratiosToggledOn) {
        strategyData.builtin_strategy_type = channelValue;
      }
    } else if (isCustomTab && targets) {
      strategyData = {
        ...baseOrderData,
        strategy_style: StrategyStyleType.CUSTOM,
        targets: isChannelRatios ? undefined : targets,
      };
    } else if (isDcaTab) {
      strategyData = {
        ...baseOrderData,
        strategy_style: StrategyStyleType.DCA,
        amount_scale: isChannelRatios ? undefined : amountScale,
        first_order_percentage:
          isChannelRatios || !firstOrderToggledOn
            ? undefined
            : firstOrderPercent,
      };
    }

    if (
      strategyData &&
      !isHedgingBot &&
      (isChannelRatios || !ratiosToggledOn)
    ) {
      strategyData.strategy_style = channelValue;
    }

    setStrategy?.(strategyData);
    const shouldValidatePrice = !isSignalsBot || pricesToggledOn;
    const pricesValid =
      !shouldValidatePrice || (!!priceDifference && !!priceScale);
    let _isValid = isValid && pricesValid;
    if (isDcaTab) {
      _isValid = _isValid && !!numOrders && (isHedgingBot || !!amountScale);
    }

    if (_isValid || !isToggledOn) {
      removeError?.(errorType);
    } else {
      addError?.(errorType);
    }
  }, [
    isToggledOn,
    pricesToggledOn,
    firstOrderToggledOn,
    ratiosToggledOn,
    orderType,
    isValid,
    parsedDcaData,
    targets,
    templateStrategy,
    isFocused,
    isHedgingBot,
  ]);
};

interface usePairsSelectorsProps {
  allSymbols: string[];
  selectedSymbols: Set<string>;
  setSelectedSymbols: (symbols: string[]) => void;
}

export const usePairsSelectors = ({
  allSymbols,
  selectedSymbols,
  setSelectedSymbols,
}: usePairsSelectorsProps) => {
  const pairToSymbols = useMemo(() => {
    const pairToSymbols: Record<string, Set<string>> = {};
    allSymbols.forEach((symbol) => {
      const pair = getPairFromSymbol(symbol);
      if (pair in pairToSymbols) pairToSymbols[pair].add(symbol);
      else pairToSymbols[pair] = new Set([symbol]);
    });
    return pairToSymbols;
  }, [allSymbols]);

  const onPairClick = useCallback(
    (pair: string, add: boolean) => {
      updateItems(
        selectedSymbols,
        pairToSymbols[pair],
        add,
        setSelectedSymbols
      );
    },
    [setSelectedSymbols, selectedSymbols, pairToSymbols]
  );

  const pairToSymbolsCount: Record<string, number> = useMemo(
    () => mapValues(pairToSymbols, (symbolsSet) => symbolsSet.size),
    [pairToSymbols]
  );

  const allPairs = useMemo(() => {
    let pairs = Object.entries(pairToSymbolsCount).sort((a, b) => {
      return b[1] - a[1];
    });
    const filteredPairs = pairs.filter((pairTuple) => pairTuple[1] > 10);
    if (filteredPairs.length > 1) {
      pairs = filteredPairs;
    }
    return pairs.slice(0, 10).map((pair) => pair[0]);
  }, [pairToSymbolsCount]);

  const isPairSelected = (pair: string) => {
    return Array.from(pairToSymbols[pair] || []).every((symbol) =>
      selectedSymbols.has(symbol)
    );
  };

  const pairOptions = useMemo(() => {
    return allPairs.slice(0, 6).map((pair) => {
      const isSelected = isPairSelected(pair);
      return {
        pair,
        isSelected,
        onClick: () => {
          onPairClick(pair, !isSelected);
        },
        numSymbols: pairToSymbolsCount[pair] || 0,
      };
    });
  }, [allPairs, selectedSymbols, pairToSymbolsCount, onPairClick]);

  return {
    pairOptions,
  };
};

export const useSetFormValues = () => {
  const { formSetters } = useContext(BotFormContext)!;

  return (
    baseFormValues?: BaseBotFormValues,
    keysToSet?: (keyof BaseBotFormValues)[],
    keysToIgnore?: (keyof BaseBotFormValues)[]
  ) => {
    setBotFormValues(formSetters, baseFormValues, keysToSet, keysToIgnore);
  };
};

export const useMergeBotValues = () => {
  const { botType } = useContext(BotFormContext)!;
  const { getAccountById, clientsData } = useClientsData();
  const { data: precisionsData, getPrecision } = usePrecisions();

  const getBotValuesOptions = (bot: BotData) => ({
    getAccountById,
    pricePrecision: getPrecision(
      PrecisionType.PRICE,
      bot.symbols?.[0],
      bot.exchange
    ),
  });

  const getMultiValue = (botKey: keyof BaseBotFormValues) => {
    return MULTI_VALUE_TOGGLE_BOT_KEYS.has(botKey)
      ? multiToggleValue
      : multiValue;
  };

  const getHedgingRangeSettings = (
    hedgingGridsData: BaseBotFormValues["hedgingGridsData"]
  ) => ({
    gridType: hedgingGridsData?.gridType,
    numGrids: hedgingGridsData?.numGrids,
    priceRange: hedgingGridsData?.priceRange,
  });

  const hasMixedHedgingRangeSettings = (
    hedgingGridsData: BaseBotFormValues["hedgingGridsData"]
  ) => {
    return hasMultiValues(getHedgingRangeSettings(hedgingGridsData));
  };

  const mergeHedgingGridsData = (
    baseBotValues: BaseBotFormValues,
    parsedValues: BaseBotFormValues
  ) => {
    if (botType !== BotType.HEDGING) return false;
    if (!baseBotValues.hedgingGridsData && parsedValues.hedgingGridsData) {
      baseBotValues.hedgingGridsData = parsedValues.hedgingGridsData;
    } else if (
      baseBotValues.hedgingGridsData &&
      parsedValues.hedgingGridsData
    ) {
      mergeValues(baseBotValues, parsedValues, "hedgingGridsData");
    }
    if (hasMixedHedgingRangeSettings(baseBotValues.hedgingGridsData)) {
      baseBotValues.entryStrategy = multiToggleValue;
    }
    return true;
  };

  const mergeObjects = (baseObject: any, otherObject: any) => {
    const allKeys = Object.keys(baseObject).concat(Object.keys(otherObject));
    allKeys.forEach((objKey) => {
      mergeValues(baseObject, otherObject, objKey);
    });
  };

  const mergeValues = (baseObject: any, otherObject: any, objKey: any) => {
    const baseValue = baseObject[objKey];
    const otherValue = otherObject[objKey];
    const otherValueMissing = otherValue === undefined || otherValue === null;
    if (
      (baseValue === undefined || baseValue === null) !== otherValueMissing ||
      (isSymbol(baseValue) && otherValueMissing)
    ) {
      baseObject[objKey] = getMultiValue(objKey);
    } else if (isSymbol(baseValue)) {
      return;
    } else if (Array.isArray(baseValue) || Array.isArray(otherValue)) {
      if (!isEqual(baseValue, otherValue)) {
        baseObject[objKey] = multiValue;
      }
    } else if (
      botType === BotType.HEDGING &&
      objKey === "entryStrategy" &&
      !isEqual(baseValue, otherValue)
    ) {
      baseObject[objKey] = multiToggleValue;
    } else if (isObject(baseValue) && isObject(otherValue)) {
      if (objKey === "account") {
        if (
          (baseValue as ClientData).client_id !==
          (otherValue as ClientData).client_id
        ) {
          baseObject.account = multiValue;
        }
      } else if (objKey === "trailingStopData") {
        if (!isEqual(baseValue, otherValue)) {
          baseObject.trailingStopData = multiToggleValue;
        }
      } else if (
        (objKey === "entryStrategy" || objKey === "takeProfitStrategy") &&
        (baseValue as StrategyData)?.strategy_style !==
          (otherValue as StrategyData)?.strategy_style
      ) {
        baseObject[objKey] = getMultiValue(objKey);
      } else {
        mergeObjects(baseValue, otherValue);
      }
    } else if (Array.isArray(baseValue) && !isEqual(baseValue, otherValue)) {
      baseObject[objKey] = multiValue;
    } else if (baseValue !== otherValue) {
      baseObject[objKey] = multiValue;
    }
  };

  const mergeBotsData = (
    baseBotValues: BaseBotFormValues | undefined,
    baseBotCheckedFields: Set<ToggleField>,
    bot: BotData
  ) => {
    if (!baseBotValues) return;
    const [parsedValues, checkedFields] = getBotValues(
      botType,
      bot,
      getBotValuesOptions(bot)
    );
    Object.keys(baseBotValues).forEach((botKey) => {
      if (
        botKey === "hedgingGridsData" &&
        mergeHedgingGridsData(baseBotValues, parsedValues)
      ) {
        return;
      }
      mergeValues(baseBotValues, parsedValues, botKey);
    });
    [...checkedFields].forEach((field) => {
      if (!baseBotCheckedFields.has(field)) baseBotCheckedFields.add(field);
    });
  };

  const getMergedBotsData = (
    botsData?: BotData[]
  ): [BaseBotFormValues | undefined, Set<ToggleField>] => {
    let _baseBotData: BaseBotFormValues | undefined;
    let _baseBotCheckedFields: Set<ToggleField> = new Set();
    if (!_baseBotData && botsData?.length) {
      [_baseBotData, _baseBotCheckedFields] = getBotValues(
        botType,
        botsData[0],
        getBotValuesOptions(botsData[0])
      );
    }
    botsData?.slice(1).forEach((bot) => {
      mergeBotsData(_baseBotData, _baseBotCheckedFields, bot);
    });
    return [_baseBotData, _baseBotCheckedFields];
  };

  return {
    getMergedBotsData,
    isReady: !!clientsData && (botType !== BotType.HEDGING || !!precisionsData),
  };
};

export const useEntries = () => {
  const { botFormType, botType } = useContext(BotFormContext)!;

  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const getDefaultStrategyValue = useDefaultStrategyValue(OrderTypes.ENTRY);
  const isSignalsBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isTradingViewBot =
    botType === BotType.TRADING_VIEW ||
    botType === BotType.TRADING_VIEW_SIGNALS;
  const isHedgingBulkEdit =
    isHedgingBot && botFormType === BotFormType.BULK_EDIT;

  const getTitle = () => {
    if (isSignalsBot) return t("labels.entry_strategy");
    else if (isGridBot) return t("tabs.grids");
    else if (isTradingViewBot || botFormType === BotFormType.BULK_EDIT)
      return isHedgingBot ? t("labels.hedging") : t("tabs.entries");
  };

  return {
    isToggleField: isTradingViewBot || isHedgingBulkEdit,
    title: getTitle(),
    getDefaultValue: isHedgingBulkEdit
      ? () => HedgingMode.RANGE
      : getDefaultStrategyValue,
  };
};

interface useOverrideFormValueProps {
  currentValue?: any;
  setFunction?: (value: any) => void;
  getDefaultValue?: () => any;
  toggleFieldType?: ToggleField;
  onPersonalClick?: () => void;
  onOverrideChange?: (overrideType?: OverrideType) => void;
  defaultChannelValue?: any;
}

export const useOverrideFormValue = ({
  currentValue,
  setFunction,
  getDefaultValue: propsGetDefaultValue,
  toggleFieldType,
  onPersonalClick,
  onOverrideChange,
  defaultChannelValue,
}: useOverrideFormValueProps) => {
  const { toggleField, toggleOverrideField, botType, addFieldToInitialize } =
    useContext(BotFormContext)!;
  const latestValueRef = useRef<any>(undefined);
  const isSignalsBot = botType === BotType.SIGNAL;

  useEffect(() => {
    if (!isSymbol(currentValue)) {
      latestValueRef.current = currentValue;
    }
  }, [currentValue]);

  const getDefaultValue = () => {
    if (
      toggleFieldType === ToggleField.TRAILING_ENTRY ||
      toggleFieldType === ToggleField.TRAILING_TAKE_PROFIT
    ) {
      return 1;
    }
    if (
      toggleFieldType === ToggleField.TAKE_PROFIT_NUM_ORDERS ||
      toggleFieldType === ToggleField.ENTRY_NUM_ORDERS
    )
      return 5;
    if (
      toggleFieldType === ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS ||
      toggleFieldType === ToggleField.ENTRY_NUM_ACTIVE_ORDERS
    )
      return 1;
    if (
      toggleFieldType === ToggleField.ENTRY_RATIOS ||
      toggleFieldType === ToggleField.TAKE_PROFIT_RATIOS
    ) {
      return {
        strategy_style: StrategyStyleType.BUILT_IN,
        builtin_strategy_type: StrategyType.EVENLY_DIVIDED,
      };
    }
    if (
      toggleFieldType === ToggleField.ENTRY_PRICE_STRATEGY ||
      toggleFieldType === ToggleField.TAKE_PROFIT_PRICE_STRATEGY
    )
      return {
        price_scale: 1,
        price_difference: 1,
      };
    if (toggleFieldType === ToggleField.FIRST_ENTRY) return undefined;
    if (toggleFieldType === ToggleField.AMOUNT_PER_TRADE)
      return {
        baseline: AmountType.PERCENTAGE,
        percent: 5,
      };
    if (toggleFieldType === ToggleField.ENTRY_GRACE_PERCENT)
      return {
        entry_grace_percent: 1,
        entry_grace_baseline: EntryGraceBaseline.PRICE_REACHED,
      };
    if (toggleFieldType === ToggleField.TAKE_PROFIT_GRACE_PERCENT) return 1;
  };

  const onOverrideTypeChange = (overrideType?: OverrideType) => {
    onOverrideChange?.(overrideType);
    let overrideValue: any = undefined;
    const overrideFieldType =
      isSignalsBot && toggleFieldType
        ? TOGGLE_FIELD_TO_OVERRIDE_KEY[toggleFieldType]
        : undefined;
    let isChecked = true;
    let isOverrideFieldChecked = true;
    let shouldOverrideValue = true;
    if (!overrideType) {
      overrideValue = propsGetDefaultValue ? propsGetDefaultValue() : undefined;
    } else if (
      overrideType === OverrideType.OFF ||
      overrideType === OverrideType.USER_CONFIGURATION
    ) {
      shouldOverrideValue = false;
      isChecked = false;
    } else if (
      overrideType === OverrideType.CHANNEL ||
      overrideType === OverrideType.DYNAMIC ||
      overrideType === OverrideType.ALL
    ) {
      isOverrideFieldChecked = false;
      overrideValue = defaultChannelValue ?? channelValue;
    } else if (
      overrideType === OverrideType.PERSONAL ||
      overrideType === OverrideType.CUSTOM
    ) {
      if (onPersonalClick) {
        onPersonalClick();
        return;
      }
      if (
        toggleFieldType &&
        [
          ToggleField.CLOSE_ON_TP_BEFORE_ENTRY,
          ToggleField.TAKE_PROFIT_DYNAMIC,
        ].includes(toggleFieldType)
      ) {
        overrideValue = true;
      } else if (latestValueRef.current) {
        overrideValue = latestValueRef.current;
      } else if (propsGetDefaultValue) {
        overrideValue = propsGetDefaultValue();
      } else if (
        (toggleFieldType &&
          toggleFieldType in TOGGLE_FIELD_TO_STRATEGY_FIELDS) ||
        toggleFieldType === ToggleField.TRAILING_ENTRY ||
        toggleFieldType === ToggleField.TRAILING_TAKE_PROFIT ||
        toggleFieldType === ToggleField.ENTRY_GRACE_PERCENT ||
        toggleFieldType === ToggleField.AMOUNT_PER_TRADE ||
        toggleFieldType === ToggleField.TAKE_PROFIT_GRACE_PERCENT
      ) {
        overrideValue = getDefaultValue();
      }
    }
    if (shouldOverrideValue) {
      setFunction?.(overrideValue);
      if (
        botType === BotType.HEDGING &&
        toggleFieldType === ToggleField.ENTRIES &&
        overrideValue === HedgingMode.RANGE
      ) {
        addFieldToInitialize?.("hedgingGridsData");
      }
    }
    if (toggleFieldType) {
      toggleField(toggleFieldType, isChecked);
    }
    if (overrideFieldType) {
      toggleOverrideField(overrideFieldType, isOverrideFieldChecked);
    }
  };

  return {
    onOverrideTypeChange,
  };
};

export const useToggleFieldParent = (
  toggleFieldType?: ToggleField,
  ignoreSymbolParent?: boolean
) => {
  const { checkedFields, formValues, botType } = useContext(BotFormContext)!;
  return getParentFieldChecked(
    botType,
    formValues,
    checkedFields,
    toggleFieldType,
    ignoreSymbolParent
  );
};

export const useSortedMainCoinsData = (skip?: boolean) => {
  const { subGroup, account } = useContext(BotFormContext)!;
  const parsedAccount = isSymbol(account) ? undefined : account;
  const exchange = parsedAccount?.exchange;
  const { mainCoinsToSignalsCount, isFetching } = useGroupMainCoinsSignalsCount(
    {}
  );
  const { sortedMainCoinsData, getMainCoinsAvailableAmountDataStatus } =
    useAccountMainCoins(
      mainCoinsToSignalsCount,
      parsedAccount?.client_id,
      exchange,
      skip
    );
  const { getHelperData } = useFormHelperData();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const title = exchange
    ? t("helper_titles.most_traded_pairs_with_exchange", { exchange })
    : t("helper_titles.most_traded_pairs");

  return {
    title,
    sortedMainCoinsData,
    showEmptyState: !subGroup || !sortedMainCoinsData?.length,
    emptyStateText: !subGroup
      ? t("empty_states.select_group_to_view_traded_pairs")
      : t("empty_states.no_signals_in_selected_exchange_last_30_days"),
    isFetching: getMainCoinsAvailableAmountDataStatus.isFetching || isFetching,
    helperData: getHelperData("mostTradedPairs"),
  };
};

export const useTakeProfits = () => {
  const { botType } = useContext(BotFormContext)!;
  const getDefaultStrategyValue = useDefaultStrategyValue(
    OrderTypes.TAKE_PROFIT
  );
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewBot =
    botType === BotType.TRADING_VIEW ||
    botType === BotType.TRADING_VIEW_SIGNALS;
  const showTakeProfitStrategy = [
    BotType.TRADING_VIEW,
    BotType.TRADING_VIEW_SIGNALS,
    BotType.SIGNAL,
  ].includes(botType);

  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const strategyTitle = isSignalsBot
    ? t("labels.take_profit_strategy")
    : t("tabs.take_profits");

  return {
    strategyTitle,
    isToggleSection: isTradingViewBot,
    showTakeProfitStrategy,
    getDefaultValue: getDefaultStrategyValue,
  };
};

export const useDefaultOrder = (
  orderType: OrderTypes.TAKE_PROFIT | OrderTypes.STOP,
  isCreateTrade?: boolean
) => {
  if (isCreateTrade) {
    return useCreateTradeDefaultOrder();
  } else {
    return useBotDefaultOrder(orderType);
  }
};

export const useBotDefaultOrder = (
  orderType: OrderTypes.TAKE_PROFIT | OrderTypes.STOP
) => {
  const {
    botType,
    defaultTakeProfit,
    defaultStop,
    setDefaultStop,
    setDefaultTakeProfit,
    addError,
    removeError,
    longShort,
    parsedEntriesAndGridTakeProfits,
    botFormType,
    checkedFields,
    marginType,
    isMarginExchange,
  } = useContext(BotFormContext)!;

  return useSharedDefaultOrder({
    orderType,
    addError,
    removeError,
    orders: parsedEntriesAndGridTakeProfits,
    checkedFields,
    defaultStop,
    setDefaultStop,
    defaultTakeProfit,
    setDefaultTakeProfit,
    longShort,
    botType,
    botFormType,
    marginType,
    isMarginExchange,
  });
};

export const useCreateTradeDefaultOrder = () => {
  const {
    defaultStop,
    setDefaultStop,
    entryOrders,
    useStopOrders,
    setPriceTargetsModified,
    setStopDistanceInputModified,
    setStopBaselineModified,
    existingQuantityOrder,
  } = useContext(TradingTerminalContext)!;

  let checkedFields = new Set<ToggleField>(undefined);
  checkedFields.add(ToggleField.ENTRIES);
  if (useStopOrders) checkedFields.add(ToggleField.STOP);

  return useSharedDefaultOrder({
    orderType: OrderTypes.STOP,
    orders: getParsedEntries(
      getOrdersWithExisting(entryOrders, existingQuantityOrder)
    ),
    checkedFields,
    defaultStop,
    setDefaultStop,
    onPercentInputChange: () => {
      setStopDistanceInputModified(true);
      setPriceTargetsModified(OrderTypes.STOP, false);
    },
    onBaselineChange: () => {
      setStopBaselineModified(true);
    },
  });
};

export const useSharedDefaultOrder = ({
  orderType,
  addError,
  removeError,
  orders,
  checkedFields,
  defaultStop,
  setDefaultStop,
  defaultTakeProfit,
  setDefaultTakeProfit,
  longShort,
  botType,
  botFormType,
  onPercentInputChange,
  onBaselineChange,
  marginType,
  isMarginExchange,
}: useSharedDefaultOrderProps) => {
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isBulkAction =
    botFormType === BotFormType.BULK_EDIT ||
    botFormType === BotFormType.BULK_CREATE;
  const isTakeProfit = orderType === OrderTypes.TAKE_PROFIT;
  const { getHelperData } = useFormHelperData();

  const setDefaultOrder = isTakeProfit ? setDefaultTakeProfit : setDefaultStop;
  const defaultOrder = isTakeProfit ? defaultTakeProfit : defaultStop;
  const errorType = isTakeProfit
    ? Error.DEFAULT_TAKE_PROFIT
    : Error.DEFAULT_STOP;
  const defaultData = isTakeProfit
    ? isTradingViewSignalsBot
      ? DEFAULT_FIRST_ENTRY_TAKE_PROFIT
      : DEFAULT_TAKE_PROFIT
    : DEFAULT_STOP;
  const { percent, baseline } =
    isSymbol(defaultOrder) || !defaultOrder ? defaultData : defaultOrder || {};

  const minStopPercent = useMemo(() => {
    if (
      checkedFields.has(ToggleField.ENTRIES) &&
      orders &&
      baseline &&
      !isSymbol(baseline) &&
      !isBulkAction &&
      !isTakeProfit
    ) {
      return getMinStopPercent(baseline, orders, botType);
    }
    return 0;
  }, [orders, baseline, checkedFields]);

  const maxStopPercent = getMaxStopPercent(
    isMarginExchange,
    botType,
    marginType
  );

  const percentErrorMessage = useMemo(() => {
    if (isTakeProfit) {
      return validateDefaultTakeProfit(defaultTakeProfit, longShort);
    } else if (!isSymbol(percent)) {
      return (
        checkedFields.has(ToggleField.STOP) &&
        validateDefaultStopOrder(percent, minStopPercent, maxStopPercent)
      );
    }
  }, [
    defaultOrder,
    longShort,
    checkedFields,
    minStopPercent,
    maxStopPercent,
    isTakeProfit,
  ]);

  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  let percentWarningMessage;
  if (
    orderType === OrderTypes.STOP &&
    !maxStopPercent &&
    !isSymbol(percent) &&
    percent &&
    parseFloat(percent) > 90
  ) {
    percentWarningMessage =
      botType === BotType.SIGNAL
        ? t("messages.warnings.default_stop_high_isolated")
        : t("messages.warnings.default_stop_high_cross_leverage");
  }

  useEffect(() => {
    if (!percentErrorMessage) {
      removeError?.(errorType);
    } else {
      addError?.(errorType);
    }
  }, [percentErrorMessage]);

  const setPercent = (percent: string) => {
    setDefaultOrder?.({ baseline, percent });
  };

  const setBaseline = (baseline: OrderBaseline) => {
    setDefaultOrder?.({ baseline, percent });
  };

  const percentIsMultiValue = isSymbol(percent);
  const percentInputData = {
    onChange: (percent: string) => {
      setPercent(percent);
      onPercentInputChange?.();
    },
    value: percentIsMultiValue ? "" : percent,
    precision: 2,
    suffix: "%",
    placeholder:
      percentIsMultiValue && percent !== channelValue ? t("labels.multi") : "0",
    bordered: true,
    title: t(isTakeProfit ? TAKE_PROFIT_PERCENT_TITLE : STOP_PERCENT_TITLE),
  };

  const baselineIsMultiValue = isSymbol(baseline);
  const baselineSelectorData = {
    value: baselineIsMultiValue ? undefined : baseline,
    onChange: (baseline: OrderBaseline) => {
      setBaseline(baseline);
      onBaselineChange?.();
    },
    title: t(isTakeProfit ? TAKE_PROFIT_BASELINE_TITLE : STOP_BASELINE_TITLE),
    placeholder: baselineIsMultiValue ? t("labels.multi") : undefined,
  };

  const percentHelperData = getHelperData(
    isTakeProfit ? "takeProfitPercent" : "stopPercent"
  );

  return {
    percentErrorMessage,
    percentInputData,
    baselineSelectorData,
    percentHelperData,
    percentWarningMessage,
  };
};

export const useRestoreChangesButton = (tab: Steps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    botFormType,
    tabToRestoreChangesData,
    botType,
    isChannelConfiguration,
  } = useContext(BotFormContext)!;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const shouldDisplay = isBulkEdit;

  const objectName = TAB_TO_OBJECT_NAME[tab];
  const resetData = tabToRestoreChangesData?.[tab];
  const isDropdown = isSignalsBot && isBulkEdit && !isChannelConfiguration;

  const dropdownOptions = [
    RestoreChangesType.TEMPLATE,
    RestoreChangesType.ORIGINAL_BOT,
  ].map((restoreType) => {
    return {
      text: t(RESTORE_CHANGES_TYPE_TO_DISPLAY[restoreType]),
      disabled: !resetData?.restoreChangesTypes.includes(restoreType),
      onClick: () => {
        resetData?.resetTab(restoreType);
      },
    };
  });

  const restoreText = t("buttons.restore_object", {
    objectName: t(objectName),
  });

  return {
    onClick: isDropdown
      ? undefined
      : () => resetData?.resetTab(RestoreChangesType.ORIGINAL_BOT),
    shouldDisplay,
    resetData,
    isDropdown,
    dropdownOptions,
    restoreText,
  };
};

export const useCompareBotValues = () => {
  const { formValues, checkedFields, overrideFields } =
    useContext(BotFormContext) || {};

  const getValues = (
    formValues: BaseBotFormValues | undefined,
    checkedFields: Set<ToggleField> | undefined,
    keys: (keyof BaseBotFormValues)[]
  ) => {
    if (!formValues) return;
    const filteredFormValues = pickBy(formValues, (_, formKey) =>
      keys.includes(formKey as keyof BaseBotFormValues)
    );
    keys.forEach((formKey) => {
      const toggleField = FORM_KEY_TO_TOGGLE_FIELD[formKey];
      const value = filteredFormValues[formKey];
      if (toggleField && !checkedFields?.has(toggleField)) {
        // @ts-ignore
        filteredFormValues[formKey] = undefined;
      } else if (Array.isArray(value)) {
        // @ts-ignore
        filteredFormValues[formKey] = [...value].sort();
      } else if (
        formKey === "entryStrategy" ||
        formKey === "takeProfitStrategy"
      ) {
        const strategy = filteredFormValues[formKey];
        if (strategy && !isSymbol(strategy) && "name" in strategy) {
          delete strategy["name"];
        }
      }
    });
    return filteredFormValues;
  };

  const getCheckedFields = (
    checkedFields: Set<ToggleField> | undefined,
    keys: (keyof BaseBotFormValues)[]
  ) => {
    const toggleFields = keys
      .map((key) => FORM_KEY_TO_TOGGLE_FIELD[key])
      .filter((field) => !!field) as ToggleField[];
    return [...toggleFields, ToggleField.TAKE_PROFIT_DYNAMIC].filter(
      (field) => {
        const parentToggleField = FIELD_TO_PARENT_FIELD[field];
        let parentChecked =
          !parentToggleField || checkedFields?.has(parentToggleField);
        return parentChecked && checkedFields?.has(field);
      }
    );
  };

  const getOverrideFields = (
    overrideFields: Set<OverrideFieldType> | undefined,
    keys: (keyof BaseBotFormValues)[]
  ) => {
    const filteredOverrideFields = keys
      .map((key) => FORM_KEY_TO_OVERRIDE_FIELD[key])
      .filter((field) => !!field) as OverrideFieldType[];
    return filteredOverrideFields.filter((field) => {
      return overrideFields?.has(field);
    });
  };

  const compareValues = (
    argsKeys: (keyof BaseBotFormValues)[] | undefined,
    originalValues: BaseBotFormValues | undefined,
    originalCheckedFields: Set<ToggleField> | undefined,
    originalOverrideFields: Set<OverrideFieldType> | undefined,
    keysToIgnore?: (keyof BaseBotFormValues)[]
  ) => {
    let keys =
      argsKeys ??
      (Object.keys(originalValues || {}) as (keyof BaseBotFormValues)[]);
    keys = keys.filter((key) => !keysToIgnore?.includes(key));
    const valuesChanged = !isEqual(
      getValues(formValues, checkedFields, keys),
      getValues(originalValues, originalCheckedFields, keys)
    );
    const toggleFieldsChanged = !!xor(
      getCheckedFields(checkedFields, keys),
      getCheckedFields(originalCheckedFields, keys)
    ).length;
    const overrideFieldsChanged = !!xor(
      getOverrideFields(overrideFields, keys),
      getOverrideFields(originalOverrideFields, keys)
    ).length;
    return valuesChanged || toggleFieldsChanged || overrideFieldsChanged;
  };

  return {
    compareValues,
  };
};

interface useBaselineSelectProps {
  orderType: OrderTypes.TAKE_PROFIT | OrderTypes.STOP;
}

export const useBaselineSelect = ({ orderType }: useBaselineSelectProps) => {
  const { botType } = useContext(BotFormContext) || {};
  const { getHelperData } = useFormHelperData();
  const isGridBot = botType === BotType.GRID;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  let baselines = [OrderBaseline.FIRST_ENTRY, OrderBaseline.AVERAGE_ENTRIES];
  if (isGridBot || !botType) {
    baselines = baselines.concat([OrderBaseline.LAST_ENTRY]);
  } else if (isTradingViewSignalsBot && orderType === OrderTypes.TAKE_PROFIT) {
    baselines = [OrderBaseline.FIRST_ENTRY];
  }

  const options = baselines.map((baseline) => {
    let name = ORDER_BASELINE_TO_DISPLAY[baseline] || "";
    if (isGridBot) {
      if (baseline === OrderBaseline.FIRST_ENTRY) name = "First Grid";
      else if (baseline === OrderBaseline.LAST_ENTRY) name = "Last Grid";
    }
    return {
      name,
      value: baseline,
    };
  });

  return {
    options,
    helperData: getHelperData(
      orderType === OrderTypes.TAKE_PROFIT
        ? "takeProfitBaseline"
        : "stopBaseline"
    ),
  };
};

interface useBotOrdersProps {
  botAmountData?: BotAmountData;
  getMinAmountByPrice?: (price: number) => number;
  symbolPriceData?: BotSymbolPriceData;
  formValues: BotFormValues;
  checkedFields: Set<ToggleField>;
  botType: BotType;
  isMobileApp?: boolean;
  useSymbolFromAlert?: boolean | MultiValue;
}

export const useBotOrders = ({
  botAmountData,
  symbolPriceData,
  getMinAmountByPrice,
  formValues,
  checkedFields,
  botType,
  isMobileApp,
  useSymbolFromAlert,
}: useBotOrdersProps) => {
  const {
    symbols,
    account,
    longShort,
    entryStrategy,
    takeProfitStrategy,
    gridsData,
    hedgingGridsData,
    defaultTakeProfit,
    defaultStop,
    longHedgingRatio,
  } = formValues;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isDcaBot = botType === BotType.ASAP;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const pricePrecision = symbolPriceData?.precision.price || 0;
  const symbol = isSymbol(symbols) ? undefined : symbols[0];
  const exchange = isSymbol(account) ? undefined : account?.exchange;
  const { socketPrice } = usePricesSocket(
    !isMobileApp && isGridBot ? symbol : undefined,
    !isMobileApp && isGridBot ? exchange : undefined
  );
  const currentPrice = socketPrice.value || symbolPriceData?.data;
  const isReverse = getIsReverse(longShort);
  const isStopEnabled = checkedFields.has(ToggleField.STOP);
  const isTakeProfitStrategyEnabled = checkedFields.has(
    ToggleField.TAKE_PROFIT_STRATEGY
  );
  const isTakeProfitsEnabled = checkedFields.has(ToggleField.TAKE_PROFITS);
  const isRangeHedgingBot = isHedgingBot && isHedgingRangeMode(formValues);

  const entriesEnabled =
    checkedFields.has(ToggleField.ENTRIES) &&
    entryStrategy !== multiToggleValue;
  const parsedEntriesAndGridTakeProfits = useMemo(() => {
    if (
      (botAmountData || isTradingViewSignalsBot) &&
      symbolPriceData &&
      !isSignalsBot
    ) {
      return getStrategyOrders(
        botAmountData,
        symbolPriceData.data,
        symbolPriceData.precision.price || 0,
        isHedgingBot ? false : isReverse,
        OrderTypes.ENTRY,
        entriesEnabled ? entryStrategy : undefined,
        useSymbolFromAlert ? undefined : getMinAmountByPrice,
        botType === BotType.GRID ? gridsData : undefined,
        isHedgingBot
          ? {
              hedgingGridsData: isRangeHedgingBot
                ? hedgingGridsData
                : undefined,
              longHedgingRatio,
            }
          : undefined
      );
    }
  }, [
    entryStrategy,
    botAmountData,
    gridsData,
    hedgingGridsData,
    isReverse,
    getMinAmountByPrice,
    entriesEnabled,
    symbolPriceData,
    isHedgingBot,
    isRangeHedgingBot,
    longHedgingRatio,
  ]);

  const parsedValidationEntries = useMemo(() => {
    if (isGridBot) return parsedEntriesAndGridTakeProfits?.slice(1);
    if (isHedgingBot)
      return getHedgingValidationOrders(parsedEntriesAndGridTakeProfits);
    return parsedEntriesAndGridTakeProfits;
  }, [isGridBot, isHedgingBot, parsedEntriesAndGridTakeProfits]);

  const [gridEntries, gridTakeProfits] = useMemo(() => {
    if (isGridBot)
      return splitGridBotEntries(
        parsedEntriesAndGridTakeProfits,
        currentPrice,
        isReverse
      );
    return [[], []];
  }, [parsedEntriesAndGridTakeProfits, isReverse, currentPrice]);

  const parsedEntries = useMemo(() => {
    if (isGridBot) {
      return gridEntries;
    }
    return parsedEntriesAndGridTakeProfits;
  }, [parsedEntriesAndGridTakeProfits]);

  const parsedTakeProfits = useMemo(() => {
    if (botType === BotType.GRID) {
      return gridTakeProfits;
    }
    const takeProfitPrice = getDefaultOrderPrice(
      defaultTakeProfit,
      parsedEntries,
      isReverse,
      OrderTypes.TAKE_PROFIT,
      botType,
      symbolPriceData?.data
    );
    if (
      isTakeProfitStrategyEnabled &&
      takeProfitStrategy &&
      (botAmountData || isTradingViewSignalsBot) &&
      !isSymbol(takeProfitStrategy)
    ) {
      return getStrategyOrders(
        botAmountData,
        takeProfitPrice,
        pricePrecision,
        isReverse,
        OrderTypes.TAKE_PROFIT,
        takeProfitStrategy
      );
    } else if (isDcaBot || isTakeProfitsEnabled) {
      return [
        {
          price: takeProfitPrice,
          ratio: 1,
          amount: 0,
          isValid: true,
          btcAmount: 0,
          usdAmount: 0,
          minAmount: 0,
        },
      ];
    }
  }, [
    botType,
    parsedEntries,
    symbolPriceData,
    isReverse,
    defaultTakeProfit,
    takeProfitStrategy,
    isTakeProfitStrategyEnabled,
    isTakeProfitsEnabled,
  ]);

  const parsedStop = useMemo(() => {
    if (!isStopEnabled) return undefined;
    return {
      price: getDefaultOrderPrice(
        defaultStop,
        parsedEntriesAndGridTakeProfits,
        isReverse,
        OrderTypes.STOP,
        botType
      ),
      ratio: 1,
      amount: 0,
      isValid: true,
      btcAmount: 0,
      usdAmount: 0,
      minAmount: 0,
    };
  }, [defaultStop, parsedEntriesAndGridTakeProfits, isReverse, isStopEnabled]);

  return {
    parsedEntries,
    parsedTakeProfits,
    parsedStop,
    parsedValidationEntries,
    parsedEntriesAndGridTakeProfits,
  };
};

export const useSummarySection = () => {
  const [sectionExpanded, setSectionExpanded] = useState(true);
  const isExpandable = false;

  const onTitleClick = isExpandable
    ? () => setSectionExpanded(!sectionExpanded)
    : undefined;

  return {
    sectionExpanded,
    onTitleClick,
    isExpandable,
  };
};

export const useBotFormDefaultAmount = () => {
  const {
    botAmountData,
    account,
    validateOrderConvertedAmount,
    parsedValidationEntries,
    getAmountTypePrecision,
    amountPrecision,
    botType,
    isChannelConfiguration,
    signalsMainCoinsData,
    useSymbolFromAlert,
  } = useContext(BotFormContext)!;
  const isSignalsBot = botType === BotType.SIGNAL;
  const { sortedMainCoinsData, isFetching: isFetchingMainCoinsData } =
    signalsMainCoinsData || {};
  const { getAccountMainCoinData, accountTotalAndAvailableAmountQuery } =
    useAccountAmount(!isSignalsBot || isChannelConfiguration);
  const selectedAccountId = isSymbol(account) ? undefined : account?.client_id;
  const exchange = isSymbol(account) ? undefined : account?.exchange;

  const getFallbackMainCoin = () => {
    if (selectedAccountId) {
      for (let coin of FALLBACK_MAIN_COINS) {
        const coinData = getAccountMainCoinData(selectedAccountId, coin);
        if (coinData.available > 0)
          return { coin, ...coinData, isFallback: true };
      }
      const defaultCoin = FALLBACK_MAIN_COINS[0];
      return {
        coin: defaultCoin,
        ...getAccountMainCoinData(selectedAccountId, defaultCoin),
        isFallback: true,
      };
    }
  };

  const groupMostTradedCoin = sortedMainCoinsData?.[0];
  const mostTradedCoin = useMemo(
    () => groupMostTradedCoin || getFallbackMainCoin(),
    [
      groupMostTradedCoin,
      selectedAccountId,
      accountTotalAndAvailableAmountQuery.isFetching,
    ]
  );
  const availableMostTradedPairAmount = mostTradedCoin?.available || 0;

  const getAvailableAmount = (
    selectedAmountType = AmountType.FIXED_USD_AMOUNT
  ) => {
    return (
      botAmountData?.convertMainCoinAmountToAmountType(
        botAmountData?.mainCoinAvailableAmount || 0,
        false,
        selectedAmountType
      ) || 0
    );
  };

  const getDefaultAmountValue = (
    selectedAmountType = AmountType.FIXED_USD_AMOUNT,
    entries = parsedValidationEntries
  ) => {
    const amountPrecision = getAmountTypePrecision(selectedAmountType);
    const minBotAmount =
      botAmountData && exchange
        ? getBotMinAmount(
            entries,
            botAmountData,
            selectedAmountType,
            amountPrecision,
            exchange,
            validateOrderConvertedAmount,
            botType,
            useSymbolFromAlert
          )
        : undefined;
    if (!isNumber(minBotAmount) || !botAmountData) return;

    let availableAmount: number | undefined;
    if (isSignalsBot) {
      availableAmount = botAmountData?.convertMainCoinAmountToAmountType(
        mostTradedCoin
          ? availableMostTradedPairAmount
          : botAmountData?.availableAmount.USDAmount,
        false,
        selectedAmountType
      );
    } else {
      availableAmount = getAvailableAmount(selectedAmountType);
    }
    const defaultAmountValue = getDefaultAmountPerTradeValue(
      availableAmount ?? 0,
      minBotAmount,
      selectedAmountType,
      selectedAmountType === AmountType.RISK_PERCENTAGE
        ? 5
        : DEFAULT_AMOUNT_PERCENT,
      amountPrecision,
      true
    );
    return {
      defaultAmountValue,
      minBotAmount,
      availableAmount,
    };
  };

  return {
    getDefaultAmountValue,
    getAvailableAmount,
    amountPrecision,
    isFetching: isFetchingMainCoinsData,
    mostTradedCoin,
  };
};

export const useStrategyError = (orderType: OrderTypes) => {
  const { errors, checkedFields, isMarginExchange, botType } =
    useContext(BotFormContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const toggleField =
    orderType === OrderTypes.ENTRY
      ? ToggleField.ENTRIES
      : ToggleField.TAKE_PROFIT_STRATEGY;
  if (!checkedFields.has(toggleField)) return;
  if (errors.includes(Error.ACTIVE_ENTRIES_AMOUNT)) {
    return getActiveEntriesAmountError(
      botType,
      true,
      errors.includes(Error.MIN_ENTRY_ORDER_AMOUNT)
    );
  } else if (errors.includes(Error.MIN_ENTRY_ORDER_AMOUNT)) {
    return getMinEntryOrderAmountError(isMarginExchange);
  } else if (errors.includes(Error.ENTRIES_RATIO_SUM)) {
    return t("messages.errors.entries_ratio_sum");
  } else if (errors.includes(Error.CONSECUTIVE_GRIDS_PRICE_DISTANCE)) {
    return t("messages.errors.min_consecutive_grids_distance", {
      min: MIN_CONSECUTIVE_GRIDS_DISTANCE,
    });
  }
};

export const useAmountError = (
  isOnboarding?: boolean,
  isCopyPublicBotSummary?: boolean,
  mostTradedCoin?: {
    isFallback?: boolean;
    total: number;
    available: number;
    coin: string;
  },
  isBacktestModal?: boolean
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    amountType,
    amountValue,
    errors,
    botAmountData,
    account,
    validateOrderConvertedAmount,
    parsedValidationEntries,
    initialBotAmountData,
    amountPrecision,
    botType,
    isChannelConfiguration,
    botGeneralData,
    amountPercentageBaseType,
    useSymbolFromAlert,
    amountCustomBaseValue,
  } = useContext(BotFormContext)!;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isHedgingBot = botType === BotType.HEDGING;
  const parsedAmount = isSymbol(amountValue) ? undefined : amountValue;
  const isPercentAmountType = getIsPercentAmountType(amountType);

  const [minBotAmount, maxBotAmount] = useMemo(() => {
    if (isChannelConfiguration) {
      let maxPercent = MAX_CHANNEL_CONFIGURATION_PERCENT;
      const maxRatio = (botGeneralData as BotGeneralData)?.sub_group_settings
        ?.max_amount_ratio_limit;
      if (typeof maxRatio === "number") {
        maxPercent = toFixed(maxRatio * 100, 2);
      }
      return [0, maxPercent];
    }
    if (
      botAmountData &&
      amountType &&
      !isSymbol(account) &&
      account &&
      !isSymbol(amountType)
    ) {
      return [
        getBotMinAmount(
          parsedValidationEntries,
          useSymbolFromAlert ? undefined : botAmountData,
          amountType,
          amountPrecision,
          account.exchange,
          validateOrderConvertedAmount,
          botType,
          useSymbolFromAlert
        ),
        getMaxBotAmount(
          parsedValidationEntries,
          botAmountData,
          useSymbolFromAlert,
          botType
        ),
      ];
    }
    return [];
  }, [
    parsedValidationEntries,
    botAmountData,
    amountType,
    amountPrecision,
    account,
    validateOrderConvertedAmount,
    initialBotAmountData,
    isChannelConfiguration,
    useSymbolFromAlert,
  ]);

  const getSignalsBotAmountErrorMessage = (isBaseline?: boolean) => {
    const available = toFixed(mostTradedCoin?.available, USD_PRECISION);
    const note =
      mostTradedCoin && !mostTradedCoin?.isFallback
        ? ` ${t("labels.most_traded_coin_note")}`
        : "";
    return t("messages.errors.signals_amount_higher_than_available", {
      what: isBaseline
        ? t("labels.percent_amount_baseline")
        : t("labels.amount"),
      coin: mostTradedCoin?.coin,
      note,
      available,
    });
  };

  const getSignalsBotErrorData = (): [string | undefined, boolean, boolean] => {
    const isHighAmount =
      parsedAmount !== undefined &&
      (isPercentAmountType
        ? maxBotAmount && parsedAmount > maxBotAmount
        : !useSymbolFromAlert &&
          mostTradedCoin?.available !== undefined &&
          parsedAmount > mostTradedCoin?.available);
    const isLowAmount =
      parsedAmount !== undefined &&
      (isPercentAmountType
        ? !parsedAmount
        : minBotAmount !== undefined && parsedAmount < minBotAmount);
    let errorMessage: string | undefined;
    if (isHighAmount) {
      if (isPercentAmountType) {
        errorMessage = t("messages.errors.value_should_be_percent_or_lower", {
          value: isChannelConfiguration ? maxBotAmount : 100,
        });
      } else {
        errorMessage = getSignalsBotAmountErrorMessage();
      }
    } else if (isLowAmount) {
      if (
        amountType === AmountType.FIXED_USD_AMOUNT ||
        amountType === AmountType.FIXED_BTC_AMOUNT
      ) {
        const suffix = getAmountTypeSuffix(amountType);
        errorMessage = t("messages.warnings.low_amount_consider_above", {
          min: minBotAmount,
          suffix,
        });
      } else {
        errorMessage = t("messages.errors.value_should_be_greater_than_zero");
      }
    }
    return [
      errorMessage,
      !!errorMessage,
      !!errorMessage && !!isPercentAmountType,
    ];
  };

  const getGeneralBotErrorData = (): [string | undefined, boolean, boolean] => {
    let errorMessage: string | undefined;
    const showGeneralAmountError =
      (errors.includes(Error.MIN_ENTRY_ORDER_AMOUNT) &&
        errors.includes(Error.ACTIVE_ENTRIES_AMOUNT)) ||
      (minBotAmount && maxBotAmount && maxBotAmount <= minBotAmount);
    if (errors.includes(Error.INSUFFICIENT_AMOUNT) && botAmountData?.mainCoin) {
      errorMessage = isBacktestModal
        ? undefined
        : t("messages.errors.available_amount_lower_than_min_order", {
            coin: botAmountData.mainCoin,
          });
    } else if (isHedgingBot && errors.includes(Error.MIN_ENTRY_ORDER_AMOUNT)) {
      errorMessage = t("messages.errors.min_hedging_order_amount");
    } else if (
      isHedgingBot &&
      errors.includes(Error.GENERAL_AMOUNT) &&
      botAmountData?.mainCoin
    ) {
      errorMessage = t("messages.warnings.amount_higher_than_available", {
        coin: botAmountData.mainCoin,
      });
    } else if (isCopyPublicBotSummary && showGeneralAmountError) {
      errorMessage = t(
        "messages.errors.incompatible_entry_strategy_choose_other_or_edit"
      );
    } else if (
      errors.includes(Error.GENERAL_AMOUNT) &&
      minBotAmount &&
      maxBotAmount &&
      !showGeneralAmountError
    ) {
      let suffix = "%";
      if (amountType === AmountType.FIXED_USD_AMOUNT) suffix = " USD";
      else if (amountType === AmountType.FIXED_BTC_AMOUNT) suffix = " BTC";
      const next = isOnboarding
        ? t("messages.suggestions.modify_amount_per_trade")
        : t("messages.suggestions.modify_amount_or_change_entries");
      if (
        amountValue &&
        !isSymbol(amountValue) &&
        amountValue >= maxBotAmount
      ) {
        errorMessage = t("messages.errors.required_amount_between", {
          min: minBotAmount,
          max: maxBotAmount,
          suffix,
          next,
        });
      } else {
        errorMessage = t("messages.errors.required_amount_at_least", {
          min: minBotAmount,
          suffix,
          next,
        });
      }
    }
    return [errorMessage, false, isHedgingBot && !!errorMessage];
  };

  const [amountError, isWarning, isError] = useMemo(() => {
    return isSignalsBot || useSymbolFromAlert
      ? getSignalsBotErrorData()
      : getGeneralBotErrorData();
  }, [
    errors,
    minBotAmount,
    maxBotAmount,
    amountValue,
    botAmountData,
    mostTradedCoin,
    useSymbolFromAlert,
  ]);

  const baselineErrorData = useMemo(() => {
    if (!isSignalsBot) return;
    let baselineError = "";
    let isWarning = false;
    let isError = false;
    const parsedCustomValue = amountCustomBaseValue
      ? parseFloat(amountCustomBaseValue)
      : undefined;
    if (amountPercentageBaseType === PercentageBaseType.CUSTOM_AMOUNT) {
      if (!amountCustomBaseValue) {
        baselineError = t("messages.errors.custom_amount_baseline_required");
        isError = true;
      } else if (!parsedCustomValue) {
        baselineError = t("messages.errors.amount_baseline_greater_than_zero");
        isError = true;
      } else if (
        mostTradedCoin?.available !== undefined &&
        parsedCustomValue > mostTradedCoin?.available
      ) {
        baselineError = getSignalsBotAmountErrorMessage(true);
        isWarning = true;
      }
    }
    return {
      baselineError,
      isWarning,
      isError,
    };
  }, [
    amountValue,
    amountType,
    amountPercentageBaseType,
    amountCustomBaseValue,
  ]);

  return {
    amountError,
    minBotAmount,
    maxBotAmount,
    isWarning,
    isError,
    baselineErrorData,
  };
};

export const useSymbolsCoins = (
  symbols: string[] | MultiValue,
  exchange: string | undefined
) => {
  const { getQuantityCoin } = useSymbolsMetadata();

  return useMemo(() => {
    if (isSymbol(symbols)) return [];
    return symbols
      ?.map((symbol) => getQuantityCoin(symbol, exchange, true))
      .filter((coin) => !!coin) as string[];
  }, [symbols]);
};

export const useStopType = () => {
  const { botGeneralData, accountsOverlapData, botFormType } =
    useContext(BotFormContext)!;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;

  const stopTypeOptions: (NumberOption & { isInvalid?: boolean })[] =
    useMemo(() => {
      let allOptions: NumberOption[] | undefined;
      let validOptionsValues: number[] | undefined = [];
      let stopTypeOptions: (NumberOption & { isInvalid?: boolean })[] =
        STOP_TYPE_OPTIONS;
      if (isBulkCreate || isBulkEdit) {
        allOptions = accountsOverlapData?.["stopType"]?.allOptions;
      } else {
        allOptions = botGeneralData?.options?.["Stop Type"]?.[0];
      }
      validOptionsValues = allOptions?.map((option) => option.value);
      if (!allOptions?.length) {
        stopTypeOptions = [];
      }
      stopTypeOptions = stopTypeOptions.map((option) => {
        const isInvalid = !validOptionsValues?.includes(option.value);
        return { ...option, isInvalid };
      });
      if (isBulkEdit) {
        stopTypeOptions = stopTypeOptions?.filter(
          (option) => !option.isInvalid
        );
      }
      return stopTypeOptions;
    }, [accountsOverlapData, botGeneralData]);

  const validStopTypeOptions = useMemo(
    () => stopTypeOptions?.filter((option) => !option.isInvalid),
    [stopTypeOptions]
  );

  return {
    validStopTypeOptions,
    stopTypeOptions,
  };
};

export const useDefaultStrategyValue = (argsOrderType?: OrderTypes) => {
  const {
    botGeneralData,
    botType,
    addFieldToInitialize,
    entryStrategy,
    takeProfitStrategy,
  } = useContext(BotFormContext)!;
  const isSignalsBot = botType === BotType.SIGNAL;

  return (_orderType?: OrderTypes) => {
    const orderType = _orderType ?? argsOrderType;
    const isEntry = orderType === OrderTypes.ENTRY;
    const strategy = isEntry ? entryStrategy : takeProfitStrategy;
    if (!orderType) return;
    addFieldToInitialize?.(isEntry ? "entryStrategy" : "takeProfitStrategy");
    const defaultStrategy = getDefaultBotStrategy(
      botGeneralData,
      orderType,
      isSignalsBot
    );
    if (!isSignalsBot || isSymbol(strategy)) return defaultStrategy;
    return {
      ...strategy,
      ...pickBy(defaultStrategy, (_, key) => {
        return [
          "strategy_style",
          "amount_scale",
          "first_order_percentage",
        ].includes(key);
      }),
    };
  };
};

interface useDefaultSubGroupConfigurationProps {
  subGroupId?: number;
  exchange?: string;
  isOnboarding?: boolean;
}

export const useDefaultSubGroupConfiguration = ({
  subGroupId,
  exchange,
  isOnboarding,
}: useDefaultSubGroupConfigurationProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const getSubGroupDefaultConfigurationQuery =
    sharedLib.sharedApi.bots.useGetSubGroupDefaultConfigurationQuery(
      subGroupId && exchange
        ? { sub_group_id: subGroupId, exchange }
        : skipToken
    );
  const defaultConfiguration = getSubGroupDefaultConfigurationQuery.data;

  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const displayText = isOnboarding
    ? t("group_configuration.applied")
    : t("group_configuration.used_for_unoverridden");

  return {
    closeModal: () => setIsModalOpen(false),
    openModal: () => setIsModalOpen(true),
    isModalOpen,
    defaultConfiguration,
    getSubGroupDefaultConfigurationQuery,
    displayText,
  };
};

export const useStrategyPrices = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    priceDifferenceSelectorData,
    priceScaleSelectorData,
    orderType,
    singleColumnLayout,
  } = useContext(BotBaseStrategiesContext)!;
  const { botType } = useContext(BotFormContext)!;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isEntry = orderType === OrderTypes.ENTRY;

  const toggleFieldData = {
    isToggleField: isSignalsBot,
    fieldType: isEntry
      ? ToggleField.ENTRY_PRICE_STRATEGY
      : ToggleField.TAKE_PROFIT_PRICE_STRATEGY,
    title: isEntry
      ? t("strategy.entries_prices")
      : t("strategy.take_profits_prices"),
  };

  return {
    toggleFieldData,
    priceDifferenceSelectorData,
    priceScaleSelectorData,
    singleColumnLayout,
  };
};

export const useStrategyNumOrders = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { numOrdersSelectorData, orderType, singleColumnLayout } = useContext(
    BotBaseStrategiesContext
  )!;
  const strategyContextData = useContext(BotStrategiesContext);
  const { botType } = useContext(BotFormContext)!;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isEntry = orderType === OrderTypes.ENTRY;
  const isDcaStrategy =
    !strategyContextData || strategyContextData?.tab === StrategyTabs.DCA;

  const toggleFieldData = {
    isToggleField: isSignalsBot,
    fieldType: isEntry
      ? ToggleField.ENTRY_NUM_ORDERS
      : ToggleField.TAKE_PROFIT_NUM_ORDERS,
    title: t("strategy.num_orders"),
  };

  return {
    toggleFieldData,
    numOrdersSelectorData: {
      ...numOrdersSelectorData,
      show: numOrdersSelectorData.show && isDcaStrategy,
    },
    singleColumnLayout,
  };
};

export const useStrategyNumActiveOrders = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { numActiveOrdersSelectorData, orderType, singleColumnLayout } =
    useContext(BotBaseStrategiesContext)!;
  const { botType } = useContext(BotFormContext)!;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isEntry = orderType === OrderTypes.ENTRY;

  const toggleFieldData = {
    isToggleField: isSignalsBot,
    fieldType: isEntry
      ? ToggleField.ENTRY_NUM_ACTIVE_ORDERS
      : ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS,
    title: t("strategy.num_active_orders"),
  };

  return {
    toggleFieldData,
    numActiveOrdersSelectorData,
    singleColumnLayout,
  };
};

export const useOverrideToggle = (overrideField: OverrideFieldType) => {
  const {
    overrideFields,
    toggleOverrideField,
    botType,
    isAdvancedView,
    isChannelConfiguration,
  } = useContext(BotFormContext)!;
  const { getHelperData } = useFormHelperData();

  const isSignalsBot = botType === BotType.SIGNAL;
  const isOverride = overrideFields.has(overrideField);
  const isChecked = !isOverride;
  const onChange = () => toggleOverrideField(overrideField, !isOverride);
  const helperData = getHelperData("useAsFallbackOnly");
  const shouldDisplay =
    isSignalsBot && isAdvancedView && !isChannelConfiguration;

  return {
    isChecked,
    onChange,
    helperData,
    isSignalsBot,
    isAdvancedView,
    shouldDisplay,
  };
};

interface useStartGroupFreeTrialModalProps {
  saveChanges: () => void;
  displayResultMessage: DisplayResultMessage;
  closeModal: () => void;
}

export const useStartGroupFreeTrialModal = ({
  saveChanges,
  displayResultMessage,
  closeModal,
}: useStartGroupFreeTrialModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { subGroup } = useContext(BotFormContext)!;

  const [isChecked, setIsChecked] = useState(false);
  const [showError, setShowError] = useState(false);

  const [
    subscribeToGroupFreeTrial,
    { isLoading: subscribeToGroupFreeTrialLoading },
  ] = sharedLib.sharedApi.configuration.useSubscribeToGroupFreeTrialMutation();
  const isFreeTrialGroup = subGroup?.isFreeTrialGroup;

  const onOkPress = () => {
    if (isChecked) {
      displayResultMessage(
        subscribeToGroupFreeTrial({ sub_group_id: subGroup?.id! }).unwrap()
      )
        .then(() => saveChanges())
        .catch(() => {});
    } else if (!showError) setShowError(true);
  };

  const confirmMarketplaceGroupData = {
    shouldDisplay: isFreeTrialGroup,
    isChecked,
    setIsChecked,
    showError: showError,
    content: t("modals.start_free_trial", {
      groupName: subGroup?.name || "",
    }),
    onOk: onOkPress,
    isLoading: subscribeToGroupFreeTrialLoading,
    onCancel: () => {
      setShowError(false);
      setIsChecked(false);
      closeModal();
    },
    isHighRisk: getIsHighRisk(subGroup?.groupData?.backtesting_score_breakdown),
  };

  return { confirmMarketplaceGroupData };
};

export const useDefaultExchangeSymbol = () => {
  const { isSymbolActiveInExchange } = useSymbolsMetadata();

  const getDefaultSymbol = useCallback(
    (exchange?: string) => {
      if (!exchange) return;
      for (let symbol of ["BTC/USDT", "BTC/USD", "BTC/USDC"]) {
        if (isSymbolActiveInExchange(symbol, exchange)) {
          return symbol;
        }
      }
    },
    [isSymbolActiveInExchange]
  );

  return {
    getDefaultSymbol,
  };
};

export const useMarginOptions = () => {
  const { getExchangeMetadata } = useExchangesMetadata();
  const { botType } = useContext(BotFormContext)!;
  const isTvBot =
    botType === BotType.TRADING_VIEW ||
    botType === BotType.TRADING_VIEW_SIGNALS;

  const getMarginOptions = useCallback(
    (
      botOptions: BotOptions | undefined,
      symbols: string[] | undefined | MultiValue,
      exchanges?: string[] | MultiValue,
      exchange?: string
    ) => {
      const useSymbolFromAlert =
        isTvBot && !isSymbol(symbols) && !symbols?.length;
      if (!useSymbolFromAlert) return getBotMarginOptions(botOptions, symbols);
      let maxMultiplier: number | undefined;
      let multiplierOptions: NumberOption[] | undefined;
      let longShortOptions: NumberOption[] | undefined;
      let _exchanges: string[] | undefined;
      if (exchanges && !isSymbol(exchanges)) {
        _exchanges = exchanges;
      } else if (exchange) {
        _exchanges = [exchange];
      }
      if (_exchanges) {
        const maxLeverages = _exchanges
          .map((exchange) => getExchangeMetadata(exchange)?.limits.leverage)
          .filter(Boolean);
        if (maxLeverages.length) {
          maxMultiplier = Math.max(...(maxLeverages as number[]));
        }
      }
      longShortOptions = botOptions?.["Long/Short"]?.[0];
      if (maxMultiplier) {
        multiplierOptions = Array.from(
          { length: maxMultiplier },
          (_, i) => i + 1
        ).map((multiplier) => {
          return { value: multiplier, name: multiplier.toString() };
        });
      }

      return {
        longShortOptions,
        multiplierOptions: longShortOptions ? multiplierOptions : undefined,
        marginTypeOptions: longShortOptions
          ? [
              {
                value: MarginTypes.ISOLATED,
                name: MARGIN_TYPE_TO_DISPLAY[MarginTypes.ISOLATED],
              },
              {
                value: MarginTypes.CROSS,
                name: MARGIN_TYPE_TO_DISPLAY[MarginTypes.CROSS],
              },
            ]
          : undefined,
      };
    },
    [botType, getExchangeMetadata]
  );

  return {
    getMarginOptions,
  };
};

export const useGetBulkEditMarginOptions = () => {
  const { getMarginOptions } = useMarginOptions();

  const getBulkEditMarginOptions = (bulkEditBotsData?: BulkEditBotData[]) => {
    let maxMultiplier: number | undefined;
    let marginTypeOptions: NumberOption[] | undefined;
    let longShortOptions: NumberOption[] | undefined;
    bulkEditBotsData?.forEach((bot) => {
      const {
        marginTypeOptions: currentMarginTypeOptions,
        multiplierOptions,
        longShortOptions: currentLongShortOptions,
      } = getMarginOptions(
        bot.options,
        bot.symbols,
        bot.exchanges,
        bot.exchange
      );
      const botMaxMultiplier =
        (!!multiplierOptions?.length &&
          multiplierOptions?.[multiplierOptions.length - 1].value) ||
        0;
      if (!maxMultiplier || botMaxMultiplier > maxMultiplier) {
        maxMultiplier = botMaxMultiplier;
      }
      if (currentMarginTypeOptions) {
        marginTypeOptions = unionBy(
          marginTypeOptions,
          currentMarginTypeOptions,
          "value"
        );
      }
      if (currentLongShortOptions) {
        longShortOptions = unionBy(
          longShortOptions,
          currentLongShortOptions,
          "value"
        );
      }
    });

    const getMultiplierOptions = () => {
      if (!maxMultiplier) return;
      const roundedMaxMultiplier = Math.floor(maxMultiplier);
      const isFloatMaxMultiplier = maxMultiplier !== roundedMaxMultiplier;
      const finalMaxMultiplier = isFloatMaxMultiplier
        ? roundedMaxMultiplier + 1
        : roundedMaxMultiplier;
      return Array(finalMaxMultiplier)
        .fill(0)
        .map((_, index) => {
          const optionValue = index + 1;
          const value =
            maxMultiplier && optionValue > maxMultiplier
              ? maxMultiplier
              : optionValue;
          return { value, name: value.toString() };
        });
    };

    return {
      longShortOptions,
      marginTypeOptions,
      multiplierOptions: getMultiplierOptions(),
    };
  };

  return {
    getBulkEditMarginOptions,
  };
};

export const useValidateMarginData = () => {
  const { getMarginOptions } = useMarginOptions();

  const validateMarginData = useCallback(
    (
      botValues: BaseBotFormValues,
      bot: { options: BotOptions | undefined } | undefined
    ) => {
      const {
        longShort: baseLongShort,
        marginType,
        multiplier,
        symbols,
        useSymbolFromAlert,
        exchanges,
        account,
      } = botValues;
      const longShort = isSymbol(baseLongShort)
        ? undefined
        : baseLongShort?.[0];
      const { marginTypeOptions, multiplierOptions, longShortOptions } =
        getMarginOptions(
          bot?.options,
          useSymbolFromAlert ? undefined : symbols,
          exchanges,
          isSymbol(account) ? undefined : account?.exchange
        );
      const longShortError =
        !!longShort &&
        longShort !== LongShort.LONG &&
        !longShortOptions?.find((option) => option.value === longShort);
      const marginTypeError =
        !!marginType &&
        marginType !== MarginTypes.ISOLATED &&
        !marginTypeOptions?.find((option) => option.value === marginType);
      const multiplierError =
        !!multiplier &&
        multiplier !== 1 &&
        !multiplierOptions?.find((option) => option.value === multiplier);
      return longShortError || marginTypeError || multiplierError;
    },
    [getMarginOptions]
  );

  return {
    validateMarginData,
  };
};
